export interface AssignmentPreference {
  wholeSquad: boolean;
  userIds: string[];
  groupIds: string[];
}

type EntityWithId = { id: string };

const storageKey = (userId: string, workspaceId: string) =>
  `calendar.assignment-preference.v1:${userId}:${workspaceId}`;

const emptyPreference = (): AssignmentPreference => ({
  wholeSquad: false,
  userIds: [],
  groupIds: [],
});

export function loadAssignmentPreference(
  userId: string,
  workspaceId: string,
  users: EntityWithId[],
  groups: EntityWithId[],
): AssignmentPreference {
  try {
    const storedValue: unknown = JSON.parse(localStorage.getItem(storageKey(userId, workspaceId)) ?? 'null');

    if (
      typeof storedValue !== 'object'
      || storedValue === null
      || Array.isArray(storedValue)
    ) {
      return emptyPreference();
    }

    const { wholeSquad, userIds, groupIds } = storedValue as Record<string, unknown>;
    if (
      typeof wholeSquad !== 'boolean'
      || !Array.isArray(userIds)
      || !userIds.every((id): id is string => typeof id === 'string')
      || !Array.isArray(groupIds)
      || !groupIds.every((id): id is string => typeof id === 'string')
    ) {
      return emptyPreference();
    }

    if (wholeSquad) {
      return { wholeSquad: true, userIds: [], groupIds: [] };
    }

    const currentUserIds = new Set(users.map(({ id }) => id));
    const currentGroupIds = new Set(groups.map(({ id }) => id));
    return {
      wholeSquad: false,
      userIds: userIds.filter(id => currentUserIds.has(id)),
      groupIds: groupIds.filter(id => currentGroupIds.has(id)),
    };
  } catch {
    return emptyPreference();
  }
}

export function saveAssignmentPreference(
  userId: string,
  workspaceId: string,
  preference: AssignmentPreference,
): void {
  try {
    localStorage.setItem(storageKey(userId, workspaceId), JSON.stringify(
      preference.wholeSquad
        ? { wholeSquad: true, userIds: [], groupIds: [] }
        : preference,
    ));
  } catch {
    // Preferences are optional when browser storage is unavailable.
  }
}
